import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PnL } from "@/lib/api";
import { presetRanges } from "@/lib/bas";
import { ArrowLeft, Printer, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { BillyMark } from "@/components/BillyLogo";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const PnLPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const presets = useMemo(() => presetRanges(), []);
  const from = params.get("from") || presets.thisFY.from;
  const to = params.get("to") || presets.thisFY.to;
  const label = params.get("label") || presets.thisFY.label;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    PnL.data(from, to)
      .then(setData)
      .catch(() => toast.error("Could not load P&L"))
      .finally(() => setLoading(false));
  }, [from, to]);

  const choose = (preset) => {
    const next = new URLSearchParams(params);
    next.set("from", preset.from);
    next.set("to", preset.to);
    next.set("label", preset.label);
    setParams(next, { replace: true });
  };

  if (loading) return <div className="text-sm text-neutral-500">Loading…</div>;
  if (!data) return null;

  const today = new Date().toLocaleDateString("en-AU");

  return (
    <div data-testid="pnl-page" className="bg-white text-black">
      {/* Action bar */}
      <div className="print:hidden border-b-2 border-black bg-white sticky top-0 z-10 -mx-5 md:-mx-8 px-5 md:px-8">
        <div className="py-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { label: "This quarter", range: presets.thisQuarter },
              { label: "Last quarter", range: presets.lastQuarter },
              { label: "This FY", range: presets.thisFY },
              { label: "Last FY", range: presets.lastFY },
            ].map((p) => {
              const active = label === p.range.label;
              return (
                <button
                  key={p.label}
                  data-testid={`pnl-preset-${p.label}`}
                  onClick={() => choose(p.range)}
                  className={`px-3 py-2 border-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                    active ? "bg-black text-white border-black" : "bg-white text-black border-neutral-300 hover:border-black"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
            <button
              data-testid="pnl-print-btn"
              onClick={() => window.print()}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
            >
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="py-8 print:p-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 border-b-2 border-black pb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 inline-flex items-center gap-2">
              <TrendingUp className="h-3 w-3" /> Profit &amp; Loss
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight mt-1">
              {label}
            </h1>
            <div className="text-sm text-neutral-700 mt-2 mono">
              {data.date_from} → {data.date_to} · Prepared {today}
            </div>
          </div>
          <BillyMark size={56} />
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-4 mt-8">
          <Tile label="Total income" value={fmt(data.total_income)} accent="green" />
          <Tile label="Total expense" value={fmt(data.total_expense)} accent="amber" />
          <Tile
            label="Net profit"
            value={fmt(data.net_profit)}
            accent={data.net_profit >= 0 ? "green" : "red"}
            sub={data.margin_pct != null ? `${data.margin_pct}% margin` : ""}
          />
        </div>

        {/* Income breakdown */}
        <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
          Income by category
        </h2>
        <CategoryTable data={data.income_by_category} total={data.total_income} accent="green" />

        <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
          Expenses by category
        </h2>
        <CategoryTable data={data.expense_by_category} total={data.total_expense} accent="amber" />

        <div className="mt-12 pt-6 border-t-2 border-black text-[11px] uppercase tracking-[0.25em] text-neutral-500 text-center">
          Generated by Billy · The Outdoor Blind Company · {today}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 18mm; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
        }
      `}</style>
    </div>
  );
};

const Tile = ({ label, value, accent, sub }) => {
  const vColors = { green: "text-green-700", red: "text-red-600", amber: "text-amber-700" };
  return (
    <div className="border-2 border-black p-5 bg-white">
      <div className="swiss-label">{label}</div>
      <div className={`font-display font-black text-3xl mono mt-1 ${vColors[accent] || ""}`}>{value}</div>
      {sub && <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
};

const CategoryTable = ({ data, total, accent }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0)
    return <div className="text-sm text-neutral-500 italic mt-4">Nothing in this period.</div>;
  const vColor = accent === "green" ? "text-green-700" : "text-amber-700";
  return (
    <table className="w-full mt-4 text-sm">
      <thead>
        <tr className="text-left border-b border-neutral-300 text-[10px] uppercase tracking-widest">
          <th className="py-2">Category</th>
          <th className="py-2 text-right">Amount</th>
          <th className="py-2 text-right">% of total</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([cat, v]) => (
          <tr key={cat} className="border-b border-neutral-100">
            <td className="py-2 font-bold">{cat}</td>
            <td className={`py-2 mono text-right ${vColor}`}>{fmt(v)}</td>
            <td className="py-2 mono text-right text-neutral-500">
              {total > 0 ? `${((v / total) * 100).toFixed(1)}%` : "—"}
            </td>
          </tr>
        ))}
        <tr className="border-t-2 border-black font-bold">
          <td className="py-3 swiss-label">Total</td>
          <td className={`py-3 mono text-right ${vColor}`}>{fmt(total)}</td>
          <td className="py-3"></td>
        </tr>
      </tbody>
    </table>
  );
};

export default PnLPage;
